import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BackButton } from "../../../components/buttons/backButton";
import { EventCardSkeleton } from "../../../components/event/eventCard";
import { TabNav } from "../../../components/tabNav";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useGetEventGuestsQuery,
  useGetEventQuery,
  useGetEventTicketsBreakdownQuery,
  useGetEventTicketsQuery,
} from "../../../redux/features/event/event.api";
import {
  formatDate,
  getEventCurrencyFromTickets,
  getRandomColor,
} from "../../../utils/helpers";

import { InfoCard } from "../../../components/cards/infoCard";
import { useEventPermissions } from "../../../customeHooks/useEventPermissions";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { useAppSelector } from "../../../redux/hooks";
import { TICKET_COLOR_OPTIONS } from "../../../utils/constants";
import { EventPermissions, TicketSalesBreakdown } from "../../../utils/types";
import { EventDetailsCard } from "./components/eventDetailsCard";
import { EventEmails } from "./components/eventEmails";
import { EventGuests } from "./components/eventGuests";
import { EventOverview } from "./components/eventOverview";
import { EventSettings } from "./components/eventSettings";
import { EventTickets } from "./components/eventTickets";

export type Tabs = "overview" | "guests" | "tickets" | "emails" | "settings";

const allTabsList: {
  title: string;
  to: Tabs;
}[] = [
  {
    title: "Overview",
    to: "overview",
  },
  {
    title: "Guests",
    to: "guests",
  },
  {
    title: "Tickets",
    to: "tickets",
  },
  {
    title: "Emails",
    to: "emails",
  },
  {
    title: "Settings",
    to: "settings",
  },
];

export const ViewSingleEvent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ticketBreakdown, setTicketBreakdown] = useState<TicketSalesBreakdown>(
    [],
  );
  const [hasPaidTickets, setHasPaidTickets] = useState(false);
  const [eventCurrency, setEventCurrency] = useState<PayoutCurrency>();
  const [userEventPermissions, setUserEventPermissions] = useState<
    EventPermissions[]
  >([]);
  const [tabList, setTabList] = useState<
    {
      title: string;
      to: Tabs;
      onClick: (t: string) => void;
    }[]
  >([]);

  const { slug } = useParams<{ slug: string }>();

  const contentRef = useRef<HTMLDivElement>(null);

  const { profile } = useAppSelector((state) => state.auth);

  const {
    data: eventResponse,
    isLoading,
    refetch: refetchEventDetails,
  } = useGetEventQuery(
    {
      slug: slug,
      authenticated: true,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: ticketPurchaseBreakdown } = useGetEventTicketsBreakdownQuery(
    {
      id: eventResponse?.data?._id!,
    },
    { refetchOnMountOrArgChange: true, skip: !eventResponse?.data?._id },
  );

  const { data: guests, isFetching: fetchingGuests } = useGetEventGuestsQuery(
    {
      id: eventResponse?.data?._id!,
      search: "",
      page: 1,
      limit: 10,
      sortOrder: "desc",
    },
    { refetchOnMountOrArgChange: true, skip: !eventResponse?.data?._id },
  );

  const {
    data: ticketsData,
    isFetching: fetchingTickets,
    refetch: refetchTickets,
  } = useGetEventTicketsQuery(
    {
      id: eventResponse?.data?._id!,
    },
    { refetchOnMountOrArgChange: true, skip: !eventResponse?.data?._id },
  );

  useEventPermissions({
    event: eventResponse?.data,
    setPermissions: setUserEventPermissions,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const selectedColors: string[] = [];

    if (ticketPurchaseBreakdown?.data) {
      setTicketBreakdown(
        ticketPurchaseBreakdown.data.map((t) => {
          const color = getRandomColor(
            TICKET_COLOR_OPTIONS,
            selectedColors,
            "sequential",
          );
          selectedColors.push(color);
          return {
            ticketId: t.ticketId,
            purchaseQuantity: t.totalQuantityPurchased,
            color: color,
          };
        }),
      );
    }
  }, [ticketPurchaseBreakdown]);

  useEffect(() => {
    setHasPaidTickets(
      ticketsData?.data?.tickets?.map((t) => t.ticketType).includes("paid") ??
        false,
    );

    if ((ticketsData?.data?.totalCount ?? 0) > 0) {
      // Use NGN as default currency to accommodate pre-currency implementation
      setEventCurrency(
        getEventCurrencyFromTickets(ticketsData?.data?.tickets!) ?? "NGN",
      );
    }
  }, [ticketsData]);

  useEffect(() => {
    if (eventResponse?.data) {
      const event = eventResponse.data;
      const isCoOrganizer = (event.coOrganizers ?? [])
        .map((c) => c.email)
        .includes(profile?.email!);
      const coOrganizerDetails = isCoOrganizer
        ? (event.coOrganizers ?? []).find((c) => c.email === profile?.email)
        : undefined;

      if (
        event.userType === "organiser" ||
        (isCoOrganizer &&
          !!coOrganizerDetails &&
          coOrganizerDetails.role === "manager")
      ) {
        setTabList(
          allTabsList.map((t) => ({
            ...t,
            onClick: (selected: string) => {
              setSearchParams({
                tab: selected,
              });
            },
          })),
        );
      } else if (
        isCoOrganizer &&
        !!coOrganizerDetails &&
        coOrganizerDetails.role === "check-in-only"
      ) {
        setTabList(
          allTabsList
            .filter((t) => ["overview", "guests"].includes(t.to))
            .map((t) => ({
              ...t,
              onClick: (selected: string) => {
                setSearchParams({
                  tab: selected,
                });
              },
            })),
        );
      } else {
        navigate("/events");
      }
    }
    // eslint-disable-next-line
  }, [eventResponse]);

  return isLoading ? (
    <EventCardSkeleton imageDimension={400} showExtraDetails className="" />
  ) : (
    <div className="flex w-full animate-fade-in flex-col gap-2">
      <BackButton
        onClick={() => {
          navigate("/events");
        }}
      />

      {!!eventResponse?.data?.isFlagged ? (
        <InfoCard
          className="!bg-lighter-red"
          leftIcon="flag"
          title="Flagged Event"
          text={
            <>
              This event was flagged for{" "}
              <b>{eventResponse?.data?.flaggedReason}</b> on{" "}
              {formatDate(eventResponse?.data?.flaggedDate)}
            </>
          }
        />
      ) : undefined}

      <EventDetailsCard
        event={eventResponse?.data}
        userEventPermissions={userEventPermissions}
        guestCount={guests?.data?.totalCount ?? 0}
      />

      {/* <AddBankDetailsCTA /> */}

      <div className="flex flex-col gap-[2px]" ref={contentRef}>
        <BgWrapper className="!rounded-b-none">
          <TabNav
            navs={tabList}
            activeTab={searchParams.get("tab") ?? "overview"}
            variant="switcher"
            className=""
            tabClassName=""
          />
        </BgWrapper>

        {["overview", null].includes(searchParams.get("tab")) &&
        tabList.map((t) => t.to).includes("overview") &&
        eventResponse?.data !== undefined ? (
          <EventOverview
            event={eventResponse?.data!}
            setTab={(tab) => {
              setSearchParams({
                tab: tab,
              });
            }}
            ticketBreakdown={ticketBreakdown}
            ticketsData={ticketsData}
            guests={guests}
            fetchingGuests={fetchingGuests}
            hasPaidTickets={hasPaidTickets}
            eventCurrency={eventCurrency!}
            refetchEventDetails={() => {
              refetchEventDetails();
            }}
            userEventPermissions={userEventPermissions}
          />
        ) : undefined}

        {searchParams.get("tab") === "guests" &&
        tabList.map((t) => t.to).includes("guests") &&
        eventResponse?.data !== undefined ? (
          <EventGuests
            event={eventResponse?.data!}
            ticketBreakdown={ticketBreakdown}
            adminView={false}
            eventCurrency={eventCurrency!}
            userEventPermissions={userEventPermissions}
          />
        ) : undefined}

        {searchParams.get("tab") === "tickets" &&
        tabList.map((t) => t.to).includes("tickets") &&
        eventResponse?.data !== undefined ? (
          <EventTickets
            event={eventResponse?.data!}
            data={ticketsData}
            fetchingTickets={fetchingTickets}
            refetchTickets={() => {
              refetchTickets();
            }}
            hasPaidTickets={hasPaidTickets}
            eventCurrency={eventCurrency!}
            userEventPermissions={userEventPermissions}
          />
        ) : undefined}

        {searchParams.get("tab") === "emails" &&
        tabList.map((t) => t.to).includes("emails") &&
        eventResponse?.data !== undefined ? (
          <EventEmails
            event={eventResponse?.data!}
            refetchEvent={() => {
              refetchEventDetails();
            }}
            totalGuests={guests?.data?.totalCount ?? 0}
            userEventPermissions={userEventPermissions}
          />
        ) : undefined}

        {searchParams.get("tab") === "settings" &&
        tabList.map((t) => t.to).includes("settings") &&
        eventResponse?.data !== undefined ? (
          <EventSettings
            event={eventResponse?.data!}
            refetchEvent={() => {
              refetchEventDetails();
            }}
            userEventPermissions={userEventPermissions}
            hasPaidTickets={hasPaidTickets}
            eventCurrency={eventCurrency!}
          />
        ) : undefined}
      </div>
    </div>
  );
};
